import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, MessageCircle, Mail, Play, Loader2, FileText, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";

const TRIGGERS = [
  ["ack_pending_hours", "PO acknowledgement pending for X hours"],
  ["ack_escalation_hours", "Ack pending X hours → escalate to admin"],
  ["no_response_hours", "No supplier response for X hours"],
  ["delivery_within_days", "Delivery due within X days"],
  ["delivery_overdue", "Delivery date is overdue"],
  ["delayed_notify", "Supplier marks PO as delayed"],
  ["mark_overdue", "Mark overdue POs & notify buyer"],
];

export default function Reminders() {
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [running, setRunning] = useState(false);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState({ name: "", trigger: "ack_pending_hours", threshold: 24, channel: "whatsapp", template_key: "ack_reminder" });

  const load = () => {
    api.get("/reminders/rules").then((r) => setRules(r.data.rules)).catch(() => {});
    api.get("/reminders/log").then((r) => setLogs(r.data.logs)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data.templates)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggle = async (rule, enabled) => {
    try {
      await api.put(`/reminders/rules/${rule.id}`, { enabled, threshold: rule.threshold });
      setRules((rs) => rs.map((r) => (r.id === rule.id ? { ...r, enabled } : r)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runEngine = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reminders/run");
      toast.success(`Reminder engine ran — ${r.data.sent} automated reminder${r.data.sent === 1 ? "" : "s"} logged`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setRunning(false); }
  };

  const createRule = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reminders/rules", { ...ruleForm, threshold: Number(ruleForm.threshold) || 0 });
      toast.success("Automation rule created");
      setRuleOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteRule = async (id) => {
    try {
      await api.delete(`/reminders/rules/${id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="reminders-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Reminders & Automation</h1>
        <p className="text-sm text-muted-foreground">Manual templates, configurable automation rules, and a complete send log.</p>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="font-heading text-base font-semibold">Automation Rules</h2>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="new-rule-btn" onClick={() => setRuleOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> New Rule</Button>
            <Button data-testid="run-engine-btn" onClick={runEngine} disabled={running} className="bg-slate-900 hover:bg-slate-800">
              {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />} Run Engine Now
            </Button>
          </div>
        </div>
        <div className="divide-y" data-testid="rules-list">
          {rules.map((r) => (
            <div key={r.id} data-testid={`rule-${r.trigger}`} className="flex items-center gap-4 px-5 py-4">
              <span className={`flex h-8 w-8 items-center justify-center rounded-md border ${r.channel === "whatsapp" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>
                {r.channel === "whatsapp" ? <MessageCircle className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
              </span>
              <div className="flex-1">
                <p className="text-sm font-medium">{r.name}</p>
                <p className="text-xs text-muted-foreground">
                  IF {r.trigger === "ack_pending_hours" && `PO acknowledgement pending for ${r.threshold}h`}
                  {r.trigger === "no_response_hours" && `no supplier response for ${r.threshold}h`}
                  {r.trigger === "delivery_within_days" && `expected delivery within ${r.threshold} days`}
                  {r.trigger === "delivery_overdue" && "delivery date is overdue"}
                  {r.trigger === "delayed_notify" && "supplier marks PO as delayed"}
                  {r.trigger === "ack_escalation_hours" && `ack pending for ${r.threshold}h → escalate`}
                  {r.trigger === "mark_overdue" && "delivery overdue → mark PO & notify buyer"}
                  {" → "}{r.channel === "whatsapp" ? "send WhatsApp" : "send email"} · template: {r.template_key.replace(/_/g, " ")}
                </p>
              </div>
              <Switch data-testid={`rule-toggle-${r.trigger}`} checked={r.enabled} onCheckedChange={(v) => toggle(r, v)} />
              <Button size="icon" variant="ghost" data-testid={`rule-delete-${r.trigger}`} onClick={() => deleteRule(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={ruleOpen} onOpenChange={setRuleOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">New Automation Rule</DialogTitle></DialogHeader>
          <form onSubmit={createRule} className="space-y-4">
            <div><Label>Rule name *</Label><Input data-testid="rule-form-name" required className="mt-1.5" value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} placeholder="e.g. Ack pending > 12h reminder" /></div>
            <div>
              <Label>Trigger</Label>
              <Select value={ruleForm.trigger} onValueChange={(v) => setRuleForm({ ...ruleForm, trigger: v })}>
                <SelectTrigger data-testid="rule-form-trigger" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {TRIGGERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Threshold (hours/days)</Label><Input data-testid="rule-form-threshold" type="number" className="mt-1.5" value={ruleForm.threshold} onChange={(e) => setRuleForm({ ...ruleForm, threshold: e.target.value })} /></div>
              <div>
                <Label>Channel</Label>
                <Select value={ruleForm.channel} onValueChange={(v) => setRuleForm({ ...ruleForm, channel: v })}>
                  <SelectTrigger data-testid="rule-form-channel" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Template</Label>
              <Select value={ruleForm.template_key} onValueChange={(v) => setRuleForm({ ...ruleForm, template_key: v })}>
                <SelectTrigger data-testid="rule-form-template" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="ack_reminder">PO Acknowledgement Reminder</SelectItem>
                  <SelectItem value="delivery_update_reminder">Delivery Update Reminder</SelectItem>
                  <SelectItem value="delivery_date_confirmation">Delivery Date Confirmation</SelectItem>
                  <SelectItem value="delay_alert">Delay Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRuleOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="rule-form-submit" className="bg-slate-900 hover:bg-slate-800">Create Rule</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Reminder Templates</h2>
        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2" data-testid="templates-grid">
          {templates.filter((t) => t.key !== "generic").map((t) => (
            <div key={t.key} data-testid={`template-${t.key}`} className="rounded-lg border p-4 transition-all duration-150 hover:-translate-y-[1px] hover:shadow-sm">
              <p className="flex items-center gap-2 text-sm font-semibold"><FileText className="h-3.5 w-3.5 text-slate-400" />{t.name}</p>
              <p className="mt-2 rounded-md bg-slate-50 p-3 font-mono text-xs leading-relaxed text-slate-600">{t.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="flex items-center gap-2 border-b px-5 py-3 font-heading text-base font-semibold"><Bell className="h-4 w-4" /> Message Log (WhatsApp + Email)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="message-log-table">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-2.5 font-medium">Time</th>
                <th className="px-5 py-2.5 font-medium">Channel</th>
                <th className="px-5 py-2.5 font-medium">To</th>
                <th className="px-5 py-2.5 font-medium">Template</th>
                <th className="px-5 py-2.5 font-medium">PO</th>
                <th className="px-5 py-2.5 font-medium">Trigger</th>
                <th className="px-5 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-muted-foreground">No messages sent yet.</td></tr>}
              {logs.map((l) => (
                <tr key={l.id} data-testid={`log-${l.id.slice(0, 8)}`} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="whitespace-nowrap px-5 py-2.5 font-mono text-xs">{fmtDateTime(l.created_at)}</td>
                  <td className="px-5 py-2.5">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${l.channel === "whatsapp" ? "bg-green-50 text-green-700 border-green-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                      {l.channel === "whatsapp" ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}{l.channel}
                    </span>
                  </td>
                  <td className="px-5 py-2.5">{l.to_name}<span className="block font-mono text-xs text-muted-foreground">{l.to_contact}</span></td>
                  <td className="px-5 py-2.5">{l.template}</td>
                  <td className="px-5 py-2.5 font-mono text-xs">{l.po_number || "—"}</td>
                  <td className="px-5 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${l.triggered_by === "automation" ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-slate-100 text-slate-600"}`}>{l.triggered_by}</span></td>
                  <td className="px-5 py-2.5"><span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-xs font-semibold text-[#92400E]">{l.status.toUpperCase()}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
